/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiField} from "./UiField";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {UiButtonConfig, UiButtonSubCommandHandler, UiButtonSubEventSource} from "../../generated/UiButtonConfig";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {UiTemplateConfig} from "../../generated/UiTemplateConfig";
import {UiColorConfig} from "../../generated/UiColorConfig";
import {createUiColorCssString} from "../util/CssFormatUtil";
import {keyCodes} from "trivial-components";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

export class UiButton extends UiField<UiButtonConfig, boolean> implements UiButtonSubEventSource, UiButtonSubCommandHandler {

	private template: UiTemplateConfig;
	private data: any;

	private _$button: JQuery;

	protected initialize(config: UiButtonConfig, targetElement: Element, context: TeamAppsUiContext) {
		this._$button = $(UiButton.getReadOnlyHtml(config, null, context));

		this.template = config.template;
		this.data = config.data;

		this._$button.click((e) => {
			this.commit(true)
		});
		this._$button.keydown((e) => {
			if (e.which === keyCodes.enter || e.which === keyCodes.space) {
				this.commit(true)
			}
		});

		this._$button.appendTo(targetElement);
	}

	isValidData(v: boolean): boolean {
		return true;
	}

	public getMainDomElement(): JQuery {
		return this._$button;
	}

	setTemplate(template: UiTemplateConfig): void {
		this.template = template;
		this._$button[0].innerHTML = this._context.templateRegistry.createTemplateRenderer(this.template).render(this.data);
	}

	setData(data: any): void {
		this.data = data;
		this._$button[0].innerHTML = this._context.templateRegistry.createTemplateRenderer(this.template).render(this.data);
	}

	setColor(color: UiColorConfig): void {
		this._$button[0].style.backgroundColor = createUiColorCssString(color);
	}

	public getFocusableElement(): JQuery {
		return this._$button;
	}

	focus(): void {
		this._$button.focus();
	}

	destroy(): void {
		this._$button.remove();
	}

	protected displayCommittedValue(): void {
		// do nothing...
	}

	getTransientValue(): boolean {
		return null;
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		UiField.defaultOnEditingModeChangedImpl(this);
	}

	public static getReadOnlyHtml(config: UiButtonConfig, value: boolean, context: TeamAppsUiContext): string {
		return `<div data-fieldName="${config.fieldName}" class="UiButton btn" style="background-color: ${createUiColorCssString(config.color)}">
			${context.templateRegistry.createTemplateRenderer(config.template).render(config.data)}
        </div>`;
	}

	getDefaultValue() {
		return false;
	}

	public valuesChanged(v1: boolean, v2: boolean): boolean {
		return false;
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiButton", UiButton);